/**
 * GlobalizationPack.jsx
 *
 * One-click download hub for the MSN-CLIQ i18n integration pack.
 * Downloads a ZIP pre-configured with API base + Gemini + Sarvam keys —
 * drop it into any React+Vite / Node+Express project and translations work immediately.
 */
import React, { useEffect, useState } from 'react';
import {
  Download, Package, Globe, Code2, Server, CheckCircle2,
  Languages, Layers, Zap, Copy, ChevronRight, FileCode2,
  RefreshCw, AlertCircle,
} from 'lucide-react';
import { API, authFetch } from '../lib/api';

// ── Quick-start code snippets per integration type ─────────────────────────────

const SNIPPETS = {
  react: {
    label: 'React + Vite',
    icon: <Code2 size={15} />,
    lang: 'jsx',
    install: `# No npm install needed — just copy 2 files into src/i18n/`,
    code: `// main.jsx
import { I18nProvider } from './i18n/manchala-i18n.react.js';

createRoot(document.getElementById('root')).render(
  <I18nProvider apiBase="MANCHALA_API_BASE" defaultLang="en-IN">
    <App />
  </I18nProvider>
);

// Any component
import { useT, LanguageSwitcher } from './i18n/manchala-i18n.react.js';

export default function Header() {
  const t = useT();
  return (
    <header>
      <h1>{t('nav.dashboard', 'Dashboard')}</h1>
      <LanguageSwitcher />   {/* built-in language switcher */}
    </header>
  );
}`,
  },
  node: {
    label: 'Node.js + Express',
    icon: <Server size={15} />,
    lang: 'js',
    install: `# No npm install needed — just copy manchala-i18n-router.js`,
    code: `// server.js
const express      = require('express');
const manchalaI18n = require('./manchala-i18n-router');  // pre-configured

const app = express();
app.use(express.json());
app.use(manchalaI18n);  // registers all routes below

app.listen(3000);

// Routes registered:
//  GET  /api/i18n/locales         — 18 supported locales
//  GET  /api/i18n/dict?lang=hi-IN — translation dictionary
//  GET  /api/i18n/formats         — number/currency/date config
//  POST /api/i18n/translate       — auto-translate (Sarvam + Gemini)
//  GET  /api/country-config?pageSize=200  — all 196 countries
//  GET  /api/country-config/IND   — single country by ISO-3 code
//  GET  /api/country-fiscal       — flat array for dropdowns`,
  },
  vanilla: {
    label: 'Vanilla JS / HTML',
    icon: <Globe size={15} />,
    lang: 'html',
    install: `<!-- No build step. Copy manchala-i18n.js to your project. -->`,
    code: `<script src="manchala-i18n.js"></script>
<script>
  ManchalaI18n.init({ apiBase: 'MANCHALA_API_BASE', defaultLang: 'en-IN' })
    .then(() => {
      console.log('i18n ready — ' + ManchalaI18n.locales().length + ' locales');
    });
</script>

<!-- Auto-bind elements — no JavaScript needed -->
<h1 data-i18n="nav.dashboard">Dashboard</h1>
<button data-i18n="signup.continue_button">Continue</button>
<span data-i18n-currency>1234.5</span>   <!-- formats as ₹1,234.50 -->`,
  },
  vue: {
    label: 'Vue 3',
    icon: <Layers size={15} />,
    lang: 'js',
    install: `# No npm install — copy manchala-i18n.js + manchala-i18n.vue.js`,
    code: `// main.js
import { createApp } from 'vue';
import App from './App.vue';
import { I18nPlugin } from './manchala-i18n.vue.js';

createApp(App)
  .use(I18nPlugin, { apiBase: 'MANCHALA_API_BASE', defaultLang: 'en-IN' })
  .mount('#app');

// Any .vue component
// {{ $t('nav.dashboard', 'Dashboard') }}
// <button @click="$setLang('hi-IN')">हिन्दी</button>`,
  },
};

const BUNDLE_FILES = [
  { name: 'manchala-i18n.js',                size: '~6 KB',  desc: 'Vanilla JS UMD loader. Works in any browser without build tools.' },
  { name: 'manchala-i18n.react.js',          size: '~3 KB',  desc: 'React Provider + useT() hook + <LanguageSwitcher />.' },
  { name: 'manchala-i18n.vue.js',            size: '~3 KB',  desc: 'Vue 3 Plugin + useI18n() composable + global $t.' },
  { name: 'manchala-i18n.express.js',        size: '~3 KB',  desc: 'Lightweight Express proxy connector with in-memory caching.' },
  { name: 'manchala-i18n-router.js',         size: '~6 KB',  desc: 'Full Express router — i18n + country config (196 countries) + auto-translate keys pre-baked.' },
  { name: 'data/translation-registry.json',  size: 'live',   desc: 'Live full export — all scopes × keys × 18 locales. Generated fresh on each download.', live: true },
  { name: 'data/translation-registry.csv',   size: 'live',   desc: 'Same registry in CSV format — open in Excel / Google Sheets for bulk editing.', live: true },
  { name: 'data/country-config.json',        size: 'live',   desc: 'All active countries with currency, locale, fiscal year, date/number formats. Generated fresh.', live: true },
  { name: 'MANIFEST.json',                   size: '~1 KB',  desc: 'Generation timestamp, API base, and full file inventory for verification.', live: true },
  { name: 'SETUP.md',                        size: '~5 KB',  desc: 'Step-by-step guide for React+Vite + Node+Express. Country config usage and field reference.' },
  { name: 'README.md',                       size: '~6 KB',  desc: 'Full API reference, locale table, REST endpoint docs.' },
  { name: 'examples/index.html',             size: '~2 KB',  desc: 'Live vanilla JS demo page you can open in any browser.' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────
function StatPill({ icon, value, label, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: 'var(--surface-2,#f8fafc)', border: '1px solid var(--border,#e2e8f0)',
      borderRadius: 10, padding: '12px 18px', flex: '1 1 160px',
    }}>
      <div style={{ color, fontSize: 20 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text,#0f172a)', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary,#64748b)', fontWeight: 600, marginTop: 2, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      </div>
    </div>
  );
}

function FeatureChip({ icon, text }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.18)',
      borderRadius: 20, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#3b82f6',
      whiteSpace: 'nowrap',
    }}>
      {icon}<span>{text}</span>
    </div>
  );
}

export default function GlobalizationPack() {
  const [stats,       setStats]       = useState(null);
  const [statsLoad,   setStatsLoad]   = useState(true);
  const [activeTab,   setActiveTab]   = useState('react');
  const [downloading, setDownloading] = useState(false);
  const [dlError,     setDlError]     = useState(null);
  const [copied,      setCopied]      = useState(null); // which snippet was copied

  useEffect(() => {
    authFetch(`${API}/i18n/bundle/stats`)
      .then(r => r.json())
      .then(d => { setStats(d); setStatsLoad(false); })
      .catch(() => setStatsLoad(false));
  }, []);

  const downloadBundle = async () => {
    setDownloading(true);
    setDlError(null);
    try {
      const res = await authFetch(`${API}/i18n/bundle/download`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt.slice(0, 160)}`);
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = 'msn-cliq-i18n-bundle.zip';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setDlError(e.message);
    } finally {
      setDownloading(false);
    }
  };

  const copySnippet = (key) => {
    const snip = SNIPPETS[key];
    const text = snip.install + '\n\n' + snip.code;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const activeSnippet = SNIPPETS[activeTab];

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Package size={22} color="#3b82f6" />
            <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text,#0f172a)', margin: 0 }}>
              Globalization Pack
            </h1>
          </div>
          <p style={{ margin: 0, color: 'var(--text-secondary,#64748b)', fontSize: 13, maxWidth: 560 }}>
            One-click integration kit for your React + Vite + Node.js + Express projects.
            API keys and licensor URL are pre-configured — drop the ZIP in and translations work immediately.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <FeatureChip icon={<Zap size={12}/>} text="Zero extra npm installs" />
            <FeatureChip icon={<CheckCircle2 size={12}/>} text="Sarvam + Gemini keys pre-baked" />
            <FeatureChip icon={<Languages size={12}/>} text="18 locales supported" />
            <FeatureChip icon={<Globe size={12}/>} text="RTL + number / currency / date" />
          </div>
        </div>

        <button
          data-testid="globalization-download-btn"
          onClick={downloadBundle}
          disabled={downloading}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: downloading ? '#94a3b8' : '#3b82f6',
            color: '#fff', border: 'none', borderRadius: 10,
            padding: '12px 22px', fontSize: 14, fontWeight: 700,
            cursor: downloading ? 'not-allowed' : 'pointer',
            boxShadow: downloading ? 'none' : '0 2px 12px rgba(59,130,246,0.35)',
            transition: 'all .15s',
            whiteSpace: 'nowrap',
            minWidth: 190,
            justifyContent: 'center',
          }}
        >
          {downloading
            ? <><RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> Packaging…</>
            : <><Download size={15} /> Download Bundle (.zip)</>
          }
        </button>
      </div>

      {dlError && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 14px', color: '#ef4444', fontSize: 13 }}>
          <AlertCircle size={15} />{dlError}
        </div>
      )}

      {/* ── Live stats ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <StatPill icon={<Languages size={18}/>}     color="#3b82f6" value={statsLoad ? '…' : (stats?.localeCount ?? '18')} label="Locales" />
        <StatPill icon={<FileCode2 size={18}/>}     color="#8b5cf6" value={statsLoad ? '…' : (stats?.keyCount    ?? '—')} label="Translation Keys" />
        <StatPill icon={<Layers size={18}/>}        color="#06b6d4" value={statsLoad ? '…' : (stats?.scopeCount  ?? '—')} label="Scopes" />
        <StatPill icon={<Package size={18}/>}       color="#10b981" value="12" label="Files in Bundle" />
      </div>

      {/* ── Bundle contents ── */}
      <div style={{ background: 'var(--surface-2,#f8fafc)', border: '1px solid var(--border,#e2e8f0)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border,#e2e8f0)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Package size={15} color="#3b82f6" />
          <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text,#0f172a)' }}>Bundle Contents</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-secondary,#64748b)', background: '#e0f2fe', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
            msn-cliq-i18n-bundle.zip
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--surface,#fff)' }}>
                {['File', 'Size', 'Purpose'].map(h => (
                  <th key={h} style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 700, color: 'var(--text-secondary,#64748b)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid var(--border,#e2e8f0)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BUNDLE_FILES.map((f, i) => (
                <tr key={f.name} style={{ borderBottom: i < BUNDLE_FILES.length - 1 ? '1px solid var(--border,#e2e8f0)' : 'none', background: i % 2 === 0 ? 'transparent' : 'rgba(248,250,252,0.6)' }}>
                  <td style={{ padding: '9px 16px', fontFamily: 'monospace', fontWeight: 600, color: f.live ? '#059669' : '#3b82f6', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {f.name}
                    {f.live && <span style={{ marginLeft: 6, fontSize: 9, background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 4, padding: '1px 5px', fontFamily: 'sans-serif', fontWeight: 700, verticalAlign: 'middle' }}>LIVE</span>}
                  </td>
                  <td style={{ padding: '9px 16px', color: 'var(--text-secondary,#64748b)', whiteSpace: 'nowrap' }}>{f.size}</td>
                  <td style={{ padding: '9px 16px', color: 'var(--text,#334155)' }}>{f.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Quick-start tabs ── */}
      <div style={{ border: '1px solid var(--border,#e2e8f0)', borderRadius: 12, overflow: 'hidden' }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border,#e2e8f0)', background: 'var(--surface-2,#f8fafc)', overflowX: 'auto' }}>
          {Object.entries(SNIPPETS).map(([key, s]) => (
            <button
              key={key}
              data-testid={`glob-tab-${key}`}
              onClick={() => setActiveTab(key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
                background: activeTab === key ? 'var(--surface,#fff)' : 'transparent',
                color: activeTab === key ? '#3b82f6' : 'var(--text-secondary,#64748b)',
                borderBottom: activeTab === key ? '2px solid #3b82f6' : '2px solid transparent',
                border: 'none', cursor: 'pointer', fontWeight: activeTab === key ? 700 : 500,
                fontSize: 13, whiteSpace: 'nowrap', transition: 'all .15s',
              }}
            >
              {s.icon}{s.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button
            data-testid="glob-copy-snippet"
            onClick={() => copySnippet(activeTab)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '8px 16px', background: 'transparent', border: 'none',
              cursor: 'pointer', color: copied === activeTab ? '#10b981' : 'var(--text-secondary,#64748b)',
              fontSize: 12, fontWeight: 600, transition: 'color .2s',
            }}
          >
            {copied === activeTab ? <CheckCircle2 size={13}/> : <Copy size={13}/>}
            {copied === activeTab ? 'Copied!' : 'Copy'}
          </button>
        </div>

        {/* Tab content */}
        <div style={{ background: '#0f172a', padding: '14px 20px' }}>
          {/* install hint */}
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#94a3b8', marginBottom: 10, whiteSpace: 'pre' }}>
            {activeSnippet.install}
          </div>
          {/* code block */}
          <pre style={{
            margin: 0, fontFamily: 'monospace', fontSize: 12.5, color: '#e2e8f0',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.65,
            maxHeight: 320, overflowY: 'auto',
          }}>
            {activeSnippet.code}
          </pre>
        </div>
      </div>

      {/* ── How it works ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 14 }}>
        {[
          { n: '1', title: 'Download ZIP', desc: 'Click the Download button. Keys and API base are already embedded — no config file to edit.', color: '#3b82f6' },
          { n: '2', title: 'Copy files', desc: 'Drop manchala-i18n.react.js + manchala-i18n.js into src/i18n/. Add manchala-i18n-router.js to your backend.', color: '#8b5cf6' },
          { n: '3', title: 'Mount & use', desc: 'Wrap your React app with <I18nProvider>. Mount the Express router. Start using useT() — done.', color: '#10b981' },
          { n: '4', title: 'Edit centrally', desc: 'All keys are managed in this Translations Registry. Changes propagate to every connected project within the cache TTL.', color: '#f59e0b' },
        ].map(step => (
          <div key={step.n} style={{
            background: 'var(--surface-2,#f8fafc)', border: '1px solid var(--border,#e2e8f0)',
            borderRadius: 10, padding: '16px 18px',
          }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: step.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, marginBottom: 10 }}>
              {step.n}
            </div>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text,#0f172a)', marginBottom: 4 }}>{step.title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary,#64748b)', lineHeight: 1.5 }}>{step.desc}</div>
          </div>
        ))}
      </div>

      {/* ── Supported locales quick table ── */}
      <div style={{ background: 'var(--surface-2,#f8fafc)', border: '1px solid var(--border,#e2e8f0)', borderRadius: 12, padding: '16px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <Languages size={15} color="#3b82f6" />
          <span style={{ fontWeight: 700, fontSize: 13 }}>Supported Locales</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { code: 'en-IN', label: 'English', badge: 'source', badgeColor: '#64748b' },
            { code: 'hi-IN', label: 'हिन्दी', badge: 'Sarvam', badgeColor: '#8b5cf6' },
            { code: 'ta-IN', label: 'தமிழ்', badge: 'Sarvam', badgeColor: '#8b5cf6' },
            { code: 'te-IN', label: 'తెలుగు', badge: 'Sarvam', badgeColor: '#8b5cf6' },
            { code: 'mr-IN', label: 'मराठी', badge: 'Sarvam', badgeColor: '#8b5cf6' },
            { code: 'bn-IN', label: 'বাংলা', badge: 'Sarvam', badgeColor: '#8b5cf6' },
            { code: 'gu-IN', label: 'ગુજરાતી', badge: 'Sarvam', badgeColor: '#8b5cf6' },
            { code: 'kn-IN', label: 'ಕನ್ನಡ', badge: 'Sarvam', badgeColor: '#8b5cf6' },
            { code: 'ml-IN', label: 'മലയാളം', badge: 'Sarvam', badgeColor: '#8b5cf6' },
            { code: 'pa-IN', label: 'ਪੰਜਾਬੀ', badge: 'Sarvam', badgeColor: '#8b5cf6' },
            { code: 'ur-IN', label: 'اردو', badge: 'Sarvam · RTL', badgeColor: '#8b5cf6' },
            { code: 'or-IN', label: 'ଓଡ଼ିଆ', badge: 'Gemini', badgeColor: '#0ea5e9' },
            { code: 'ar-AE', label: 'العربية', badge: 'Gemini · RTL', badgeColor: '#0ea5e9' },
            { code: 'de-DE', label: 'Deutsch', badge: 'Gemini', badgeColor: '#0ea5e9' },
            { code: 'fr-FR', label: 'Français', badge: 'Gemini', badgeColor: '#0ea5e9' },
            { code: 'it-IT', label: 'Italiano', badge: 'Gemini', badgeColor: '#0ea5e9' },
            { code: 'es-ES', label: 'Español', badge: 'Gemini', badgeColor: '#0ea5e9' },
            { code: 'el-GR', label: 'Ελληνικά', badge: 'Gemini', badgeColor: '#0ea5e9' },
          ].map(l => (
            <div key={l.code} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'var(--surface,#fff)', border: '1px solid var(--border,#e2e8f0)',
              borderRadius: 8, padding: '6px 10px', fontSize: 12,
            }}>
              <span style={{ fontWeight: 700, color: 'var(--text,#0f172a)' }}>{l.label}</span>
              <span style={{ color: 'var(--text-secondary,#94a3b8)', fontSize: 10 }}>{l.code}</span>
              <span style={{ background: l.badgeColor + '18', color: l.badgeColor, borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>{l.badge}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Footer download CTA ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14,
        background: 'linear-gradient(135deg,rgba(59,130,246,0.07),rgba(139,92,246,0.07))',
        border: '1px solid rgba(59,130,246,0.2)', borderRadius: 12, padding: '20px 24px',
      }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--text,#0f172a)', marginBottom: 4 }}>
            Ready to integrate?
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary,#64748b)' }}>
            {(stats?.keyCount || '—')} translation keys · {(stats?.localeCount || 18)} locales · Gemini + Sarvam AI pre-wired
          </div>
        </div>
        <button
          data-testid="globalization-download-btn-footer"
          onClick={downloadBundle}
          disabled={downloading}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: downloading ? '#94a3b8' : '#3b82f6',
            color: '#fff', border: 'none', borderRadius: 10,
            padding: '11px 20px', fontSize: 13, fontWeight: 700,
            cursor: downloading ? 'not-allowed' : 'pointer',
            transition: 'background .15s', whiteSpace: 'nowrap',
          }}
        >
          {downloading
            ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Packaging…</>
            : <><Download size={14} /> Download msn-cliq-i18n-bundle.zip</>
          }
        </button>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
